import { useCallback, useEffect, useState } from "react";
import {
  Backdrop,
  Button,
  Divider,
  LogoLoader,
  NewTableAdv,
  PageHeader,
  PageTitle,
} from "../../../../../../components";
import { mapTableRows } from "../../../../../../utils";
import axiosRequest from "../../../../../../redux/slices/universal/axiosService";
import { formatCurrency } from "../../../../../../utils/stringUtils";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePagination } from "../../../../../../hooks";
import { ENDPOINTS } from "../../../../../../redux/apiConfig";
import { exactUTCDate } from "../../../../../../utils/dateFormator";

const Activities = () => {
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState({
    paymentsData: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const { page, limit, setPage, handleLimitChange } = usePagination(10);
  const { employeeProfileUiid } = useSelector((state) => state.employee);

  const activitiesColumns = [
    {
      key: "payDate",
      label: "Pay Date",
    },
    { key: "payCycle", label: "Pay Cycle" },
    {
      key: "timesheets",
      label: "Timesheet Info",
      render: (row) => (
        <div className="flex flex-col gap-2">
          {row?.timesheets?.map((ts) => (
            <div key={ts.id} className="flex justify-between text-sm gap-2">
              {/* {ts?.isUnpaid ? "Unpaid" : ""}*/}
              <div className="w-8 h-6 leading-none text-sm font-medium aspect-square rounded-full flex items-center justify-center bg-primary/10 text-primary border border-primary">
                {ts?.placementId?.clientPlacement?.placement || "-"}
              </div>
              <Divider orientation="vertical" />
              <div className="">
                {ts?.placementId?.jobInfo?.billClient?.businessName || "-"}
              </div>
              <Divider orientation="vertical" />
              <div className="">
                {`${exactUTCDate(ts?.period?.from)} - ${exactUTCDate(ts?.period?.to)}`}
              </div>
              <Divider orientation="vertical" />
              <div>
                {formatCurrency(
                    ts?.amounts?.currentPayrollDeduction
                      ? ts?.amounts?.currentPayrollDeduction
                      : ts?.amounts?.paid
                )}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    { key: "totalPayrollTSAmount", label: "Total Timesheet Amount" },
  ];

  // Make sure your data structure matches the column keys
  // const tablerowData = otherpaymentData
  const fetchActivities = useCallback(async () => {
    try {
      const payload = {
        coll: "Payroll",
        lim: limit,
        pg: page,
        ref: "TimesheetWithPlacement UnpaidTimesheetWithPlacement",
        fil: JSON.stringify({
          employeeId: { type: "NONE", value: employeeProfileUiid },
          status: { type: "NONE", value: 4 },
        }),
        sort: JSON.stringify({
          payDay: -1,
        }),
      };
      const [payrolls] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.PAYROLL.GET_ALL,
          params: payload,
        }),
      ]);
      const activitiesForEmployees = payrolls?.value?.data?.data || [];

      setData((prev) => ({
        ...prev,
        paymentsData: activitiesForEmployees?.map((e) => {
          const paidTs = e?.timesheets?.ids || [];
          const unpaidTs =
            e?.timesheets?.unpaidIds?.map((ts) => ({
              ...ts,
              isUnpaid: true,
              amounts: {
                ...ts.amounts,
                // current setup to use for temporary and to be chnaged after backend updtaes completely
                currentPayrollDeduction:
                  ts?.amounts?.adjustment?.filter(a=>a.paid)?.find((a) => a?.payrollId === e?.uiid)
                    ?.amount ?? 0,
              },
            })) || [];
          const allTS = paidTs.concat(unpaidTs);
          return {
            uiid: e?.uiid,
            payDate: exactUTCDate(e?.paymentInfo?.on),
            payCycle: `${exactUTCDate(e?.payCycle?.from)} - ${exactUTCDate(e?.payCycle?.to)}`,
            timesheets: allTS,
            totalPayrollTSAmount: formatCurrency(e?.timesheets?.totalPay || 0),
          };
        }),
      }));
    } catch (error) {
      console.error("Error in fetchInvoices:", error);
    }
  }, [page, limit]);

  const fetchTotalCountForActivities = async () => {
    try {
      setIsLoading(true);
      const payload = {
        coll: "Payroll",
        lim: limit,
        pg: page,
        fil: JSON.stringify({
          employeeId: { type: "NONE", value: employeeProfileUiid },
          status: { type: "NONE", value: 4 },
        }),
      };
      const total = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.PAYROLL.GET_TOTAL_COUNT,
        params: payload,
      });
      console.log({ total });
      setTotalCount(total?.data?.data || 0);
    } catch (error) {
      console.error("Error in fetchTotalCountForActivities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTotalCountForActivities();
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [page, limit]);

  const mappedRows = mapTableRows(data.paymentsData, activitiesColumns);

  return (
    <div>
      <PageHeader
        Section={
          <div className="flex my-3">
            <PageTitle title={"Payroll Activities"} tag="h5" />
          </div>
        }
      />
      <div className="flex justify-end m-4">
        <Button
          onClick={() => navigate(-1)}
          title={"Back"}
          size="sm"
          icon={"arrow_left_alt"}
          variant={"outlined"}
        />
      </div>
      <NewTableAdv
        showSlNo
        columns={activitiesColumns}
        fixedCol={2}
        rows={mappedRows}
        serverPagination={true}
        totalCount={totalCount}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={handleLimitChange}
        toolbarRight={{
          search: true,
          filter: true,
          download: true,
          add: [],
        }}
      />
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Activities;
